import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { env } from '@/config/env';
import { tokenStorage } from './token-storage';
import { getAdminPasskey } from '@/services/rateApi.service';
import { getAnyAccountKey } from '@/services/accountSalaryKey';

type RetriableConfig = InternalAxiosRequestConfig & { _retried?: boolean };

let refreshPromise: Promise<string | null> | null = null;
let onAuthFailure: (() => void) | null = null;

export function setAuthFailureHandler(handler: () => void) {
  onAuthFailure = handler;
}

async function performRefresh(client: AxiosInstance): Promise<string | null> {
  const refreshToken = tokenStorage.getRefreshToken();
  if (!refreshToken) return null;
  try {
    const res = await client.post<{ data: { accessToken: string; refreshToken: string } }>(
      '/auth/refresh',
      { refreshToken },
      { _retried: true } as unknown as InternalAxiosRequestConfig,
    );
    tokenStorage.setTokens(res.data.data.accessToken, res.data.data.refreshToken);
    return res.data.data.accessToken;
  } catch {
    tokenStorage.clear();
    return null;
  }
}

export const api: AxiosInstance = axios.create({
  baseURL: env.API_URL,
  timeout: 15000,
});

api.interceptors.request.use((config) => {
  const token = tokenStorage.getAccessToken();
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // The Rate API key the salary endpoints act with. The server holds none of its own,
  // so anything that needs a pay rate is priced with whatever key the caller unlocked.
  //
  // An admin's key is preferred when both are present: it is scoped to every employee,
  // so it also covers the admin's own payslip, while an employee key would 404 on
  // anyone else's row. Only sent on payroll routes — no other endpoint has any use for
  // it, and a credential should not ride along on requests that ignore it.
  if (config.headers && config.url?.includes('/payroll/')) {
    const rateKey = getAdminPasskey() || getAnyAccountKey();
    if (rateKey) config.headers['x-rate-passkey'] = rateKey;
  }

  // The backend sends no-store, but that only stops responses entering a cache from now
  // on. Installed clients already hold entries their WebView cached before that shipped,
  // and those are only reachable by asking. Both halves are needed.
  if (config.headers) {
    config.headers['Cache-Control'] = 'no-cache';
    config.headers['Pragma'] = 'no-cache';
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const config = error.config as RetriableConfig | undefined;
    if (!config || error.response?.status !== 401 || config._retried) {
      return Promise.reject(error);
    }
    if (config.url?.includes('/auth/login') || config.url?.includes('/auth/refresh')) {
      return Promise.reject(error);
    }

    config._retried = true;
    if (!refreshPromise) refreshPromise = performRefresh(api);
    const newToken = await refreshPromise;
    refreshPromise = null;

    if (!newToken) {
      onAuthFailure?.();
      return Promise.reject(error);
    }

    if (config.headers) config.headers.Authorization = `Bearer ${newToken}`;
    return api.request(config);
  },
);
